import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";

import { enums } from "../../constants/enum";
import useItemService from "../../services/api/itemService";

import { Button } from "../Button";
import { SearchIcon, CloseIcon, PasteIcon } from "../../assets/icons";
import { PriceInput, SelectInput, TextInput } from "../forms";
import "./ItemFilters.scss";

export function ItemFilters({
  initialValues,
  onSubmit,
  onClear,
  withItemCode,
}) {
  const { queryKey, getItemStatistics } = useItemService();

  const { data: itemStatistics, error } = useQuery({
    queryKey: [queryKey, "statistics"],
    queryFn: () => getItemStatistics(),
    retry: 1,
  });

  useEffect(() => {
    if (error) {
      toast.error(
        error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    }
  }, [error]);
  return (
    <div className="inventory-filters">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, resetForm }) => (
          <Form>
            <TextInput
              label="Search by Item Name"
              name="search"
              placeholder="Enter item name"
              type="search"
            />

            <div className="control-row row-1">
              <div className="item-types">
                <span>Item Type</span>

                <div className="button-group">
                  <button
                    className={`btn item-type-button ${
                      values.category === enums.ITEM_FILTER_CATEGORIES.ALL
                        ? "active"
                        : ""
                    }`}
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        "category",
                        enums.ITEM_FILTER_CATEGORIES.ALL,
                      )
                    }
                  >
                    <span>ALL</span>
                    <span>{itemStatistics?.totalItems}</span>
                  </button>

                  <button
                    className={`btn item-type-button ${
                      values.category === enums.ITEM_FILTER_CATEGORIES.LENSE
                        ? "active"
                        : ""
                    }`}
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        "category",
                        enums.ITEM_FILTER_CATEGORIES.LENSE,
                      )
                    }
                  >
                    <span>LENSES</span>
                    <span>{itemStatistics?.lenses}</span>
                  </button>

                  <button
                    className={`btn item-type-button ${
                      values.category === enums.ITEM_FILTER_CATEGORIES.FRAME
                        ? "active"
                        : ""
                    }`}
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        "category",
                        enums.ITEM_FILTER_CATEGORIES.FRAME,
                      )
                    }
                  >
                    <span>FRAMES</span>
                    <span>{itemStatistics?.frames}</span>
                  </button>

                  <button
                    className={`btn item-type-button ${
                      values.category ===
                      enums.ITEM_FILTER_CATEGORIES.ACCESSORIES
                        ? "active"
                        : ""
                    }`}
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        "category",
                        enums.ITEM_FILTER_CATEGORIES.ACCESSORIES,
                      )
                    }
                  >
                    <span>ACCESSORIES</span>
                    <span>{itemStatistics?.accessories}</span>
                  </button>

                  <button
                    className={`btn item-type-button ${
                      values.category === enums.ITEM_FILTER_CATEGORIES.SUNGLASS
                        ? "active"
                        : ""
                    }`}
                    type="button"
                    onClick={() =>
                      setFieldValue(
                        "category",
                        enums.ITEM_FILTER_CATEGORIES.SUNGLASS,
                      )
                    }
                  >
                    <span>SUNGLASSES</span>
                    <span>{itemStatistics?.sunglasses}</span>
                  </button>
                </div>
              </div>

              <SelectInput
                className="stock-status"
                label="Stock Status"
                name="stockStatus"
                values={[
                  { value: enums.ITEM_FILTER_STOCK_STATUS.ALL, label: "ANY" },
                  {
                    value: enums.ITEM_FILTER_STOCK_STATUS.READY,
                    label: "READY",
                  },
                  {
                    value: enums.ITEM_FILTER_STOCK_STATUS.PRE_ORDER,
                    label: "PRE-ORDER",
                  },
                ]}
              />
            </div>

            {/* LENSE FILTERS */}
            {values.category === enums.ITEM_FILTER_CATEGORIES.LENSE ? (
              <div className="input-group">
                <TextInput name="sph" placeholder="Enter a value" label="SPH" />
                <TextInput name="cyl" placeholder="Enter a value" label="CYL" />
                <TextInput name="add" placeholder="Enter a value" label="ADD" />
              </div>
            ) : null}

            {/* FRAME FILTERS */}
            {values.category === enums.ITEM_FILTER_CATEGORIES.FRAME ? (
              <div className="input-group">
                <TextInput
                  name="lenseDiagonal"
                  placeholder="Enter a value"
                  label="Lense Diagonal"
                />
                <TextInput
                  name="bridge"
                  placeholder="Enter a value"
                  label="Bridge"
                />
                <TextInput
                  name="armLength"
                  placeholder="Enter a value"
                  label="Arm Length"
                />
              </div>
            ) : null}

            {/* ACCESSORY FILTERS */}
            {values.category === enums.ITEM_FILTER_CATEGORIES.ACCESSORIES ? (
              <div className="input-group">
                <SelectInput
                  name="accessoryType"
                  label="Accessory Type"
                  values={[
                    { label: "Select Accessory Type", value: "" },
                    ...(itemStatistics?.accessoryTypes?.map(
                      (accessoryType) => ({
                        label: accessoryType,
                        value: accessoryType,
                      }),
                    ) || []),
                  ]}
                />

                <SelectInput
                  name="material"
                  label="Material"
                  values={[
                    { label: "Select Material", value: "" },
                    ...(itemStatistics?.accessoryMaterials?.map((material) => ({
                      label: material,
                      value: material,
                    })) || []),
                  ]}
                />
                <SelectInput
                  name="color"
                  label="Color"
                  values={[
                    { label: "Select Color", value: "" },
                    ...(itemStatistics?.accessoryColors?.map((color) => ({
                      label: color,
                      value: color,
                    })) || []),
                  ]}
                />
              </div>
            ) : null}

            {/* SUNGLASS FILTERS */}
            {values.category === enums.ITEM_FILTER_CATEGORIES.SUNGLASS && (
              <>
                <div className="input-group">
                  <SelectInput
                    name="gender"
                    label="Gender"
                    values={[
                      { label: "Select Gender", value: "" },
                      { label: "Unisex", value: enums.GENDER.UNISEX },
                      { label: "Male", value: enums.GENDER.MALE },
                      { label: "Female", value: enums.GENDER.FEMALE },
                    ]}
                  />
                  <SelectInput
                    name="sunglassFrameColor"
                    label="Frame Color"
                    values={[
                      { label: "Select Color", value: "" },
                      ...(itemStatistics?.sunglassFrameColors?.map((color) => ({
                        label: color,
                        value: color,
                      })) || []),
                    ]}
                  />
                </div>

                <div className="input-group">
                  <SelectInput
                    name="lenseType"
                    label="Lense Type"
                    values={[
                      { label: "Select Lense Type", value: "" },
                      ...(itemStatistics?.sunglassLensTypes?.map((type) => ({
                        label: type,
                        value: type,
                      })) || []),
                    ]}
                  />
                </div>
              </>
            )}

            <div className="control-row row-2">
              <SelectInput
                className="sort-by"
                label="Sort By"
                name="sort"
                values={[
                  {
                    value: enums.ITEM_SORTING_OPTIONS.NEWEST_FIRST,
                    label: "NEWEST FIRST",
                  },
                  {
                    value: enums.ITEM_SORTING_OPTIONS.OLDEST_FIRST,
                    label: "OLDEST FIRST",
                  },
                  {
                    value: enums.ITEM_SORTING_OPTIONS.NAME_A_Z,
                    label: "ALPHABETICAL: A-Z",
                  },
                  {
                    value: enums.ITEM_SORTING_OPTIONS.NAME_Z_A,
                    label: "ALPHABETICAL: Z-A",
                  },
                  {
                    value: enums.ITEM_SORTING_OPTIONS.PRICE_LOW_TO_HIGH,
                    label: "PRICE: LOW TO HIGH",
                  },
                  {
                    value: enums.ITEM_SORTING_OPTIONS.PRICE_HIGH_TO_LOW,
                    label: "PRICE: HIGH TO LOW",
                  },
                ]}
              />

              <div className="price-range">
                <PriceInput
                  label="Minimum price"
                  name="minPrice"
                  placeholder="Minimum price"
                />
                <div className="separator"></div>
                <PriceInput
                  label="Maximum price"
                  name="maxPrice"
                  placeholder="Maximum price"
                />
              </div>
            </div>

            {withItemCode ? (
              <div className="control-row row-3">
                <TextInput
                  name="itemCode"
                  label="Item Code"
                  placeholder="Type here"
                />
                <Button
                  type="button"
                  text="Paste"
                  icon={<PasteIcon />}
                  onClick={async () => {
                    const text = await navigator.clipboard.readText();
                    setFieldValue("itemCode", text);
                  }}
                />
              </div>
            ) : null}

            <div className="form-actions">
              <Button text="Search" icon={<SearchIcon />} type="submit" />
              <Button
                text="Clear"
                icon={<CloseIcon />}
                type="reset"
                outlined
                className="clear-btn"
                onClick={() => {
                  resetForm();
                  if (onClear) onClear();
                }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
